import PropTypes from 'prop-types';
import React, { useContext, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { LoadingContext, M_StateActions, MTable } from 'src/M_Components';

export default function ContactConfig({ roles, onCreateTab, toastMessages }) {
    const ctx = useContext(LoadingContext);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [refresh, setRefresh] = useState(false);

    // memo to avoid recreating the array on each render
    const removeContextItems = useMemo(() => ['Print', 'Copy'], []);

    // Route for the Contact Configuration CRUD operations
    const CRUD_ROUTE = '/home/contacts/contactConfigCrud';

    //============================================================================================================
    // Function to handle the creation of a new route
    // This function creates a new route for the contact configuration CRUD based on the operation type
    //============================================================================================================
    const openContactCrud = useCallback(
        (operation, data) => {
            dispatch(M_StateActions.addM_State({ key: 'operation', value: operation })); // Set the operation type

            if (['Edit', 'Copy', 'View'].includes(operation)) { // If operation is Edit, Copy or View, needs ContactLine data
                if (!data) {
                    toastMessages('error', '', t('SelectedLine'));
                    return;
                }
                dispatch(M_StateActions.addM_State({ key: 'ContactLine', value: data })); // Set the ContactLine data
            }

            // Define the tab label based on the operation type
            const tabLabel =
                operation === 'Create'
                    ? t('LabelNewContact')
                    : operation === 'View'
                    ? `${t('LabelViewContact')} "${data?.Name ?? ''}"`
                    : `${t('LabelEditContact')} "${data?.Name ?? ''}"`;

            onCreateTab(CRUD_ROUTE, tabLabel);
        },
        [dispatch, onCreateTab, t, toastMessages]
    );

    //============================================================================================================
    // Memoized map of functions to replace default actions in the MTable
    //============================================================================================================
    const replaceFunctions = useMemo(
        () =>
            new Map([
                ['View', (row) => openContactCrud('View', row)],
                ['Create', () => openContactCrud('Create')],
                ['Edit', (row) => openContactCrud('Edit', row)],
            ]),
        [openContactCrud]
    );

    return (
        <div className='divGlobalSettings'>
            <div className='subTitleSettings'>
                <div>
                    <div className="titleSettings" style={{ color: ctx.secondaryColor }}>{t('Contacts')}</div>
                    <div className='labelSubtitleSettings'>{t('InfoContacts')}</div>
                </div>
            </div>
            <MTable // dynamic table for Contacts
                filtersOnTable // Enable filters on the table
                entityName={'Contacts'}
                roleName={'Accounts'}
                rolePermissions={roles?.map(vl => vl.Value)} // User roles for permission management
                removeContextItems={removeContextItems}
                refresh={refresh}
                setRefresh={setRefresh}
                useSessionStorage={false}
                replaceFunctions={replaceFunctions} // Replace default functions with custom ones
            />
        </div>
    );
}

ContactConfig.propTypes = {
    roles: PropTypes.array,
    onCreateTab: PropTypes.func,
    toastMessages: PropTypes.func
};
